import React, { useContext, useState, useEffect } from "react";
import {
  FormBuilderContextProvider,
  FormBuilderContext,
} from "../context/context";
import { StyledEngineProvider } from "@mui/material/styles";
import FieldGroupsAccordion from "../main-components/FieldGroupsAccordion";

import FormPreview from "./FormPreview";
import "./styles.scss";
import { staticStyles, resetStyles, htmlCode } from "../assets/constants";
import { generateScript } from "../hooks";
import { fontList } from "../resources/fontList";

const FormBuilderScreen = ({ children, name }) => {
  const { state } = useContext(FormBuilderContext);
  const [isCopied, setIsCopied] = useState(false);

  const [labelFlexBasis, setLabelFlexBasis] = useState('auto');
  const [inputFlexBasis, setInputFlexBasis] = useState('auto');
  // Update flex basis based on alignment selection
  useEffect(() => {
    if (state.fieldLabelAlignment === 'row') {
      setLabelFlexBasis('15%');
      setInputFlexBasis('85%');
    } else {
      setLabelFlexBasis('auto'); // Or any other value for the 'column' layout
      setInputFlexBasis('auto'); // Or any other value for the 'column' layout
    }
  }, [state.fieldLabelAlignment]);

  const [inputFontImportUrl, setInputFontImportUrl] = useState('');
  useEffect(() => {
    if (state.inputFontFamily) {
      const fontFamilyLabel = fontList.find(font => font.value === state.inputFontFamily)?.label || 'sans-serif';
      const formattedFontLabel = fontFamilyLabel.replace(/\s+/g, '+');
      setInputFontImportUrl(`@import url('https://fonts.googleapis.com/css2?family=${formattedFontLabel}:wght@100;200;300;400;500;600;700;800;900&display=swap');`);
    }
  }, [state.inputFontFamily]);

  const [selectFontImportUrl, setSelectFontImportUrl] = useState('');
  useEffect(() => {
    if (state.selectFontFamily) {
      const fontFamilyLabel = fontList.find(font => font.value === state.selectFontFamily)?.label || 'sans-serif';
      const formattedFontLabel = fontFamilyLabel.replace(/\s+/g, '+');
      setSelectFontImportUrl(`@import url('https://fonts.googleapis.com/css2?family=${formattedFontLabel}:wght@100;200;300;400;500;600;700;800;900&display=swap');`);
    }
  }, [state.selectFontFamily]);

  const [textAreaFontImportUrl, setTextAreaFontImportUrl] = useState('');
  useEffect(() => {
    if (state.textAreaFontFamily) {
      const fontFamilyLabel = fontList.find(font => font.value === state.textAreaFontFamily)?.label || 'sans-serif';
      const formattedFontLabel = fontFamilyLabel.replace(/\s+/g, '+');
      setTextAreaFontImportUrl(`@import url('https://fonts.googleapis.com/css2?family=${formattedFontLabel}:wght@100;200;300;400;500;600;700;800;900&display=swap');`);
    }
  }, [state.textAreaFontFamily]);

  const [placeholderFontImportUrl, setPlaceholderFontImportUrl] = useState('');
  useEffect(() => {
    if (state.placeholderFontFamily) {
      const fontFamilyLabel = fontList.find(font => font.value === state.placeholderFontFamily)?.label || 'sans-serif';
      const formattedFontLabel = fontFamilyLabel.replace(/\s+/g, '+');
      setPlaceholderFontImportUrl(`@import url('https://fonts.googleapis.com/css2?family=${formattedFontLabel}:wght@100;200;300;400;500;600;700;800;900&display=swap');`);
    }
  }, [state.placeholderFontFamily]);

  const [submitFontImportUrl, setSubmitFontImportUrl] = useState('');
  useEffect(() => {
    if (state.submitFontFamily) {
      const fontFamilyLabel = fontList.find(font => font.value === state.submitFontFamily)?.label || 'sans-serif';
      const formattedFontLabel = fontFamilyLabel.replace(/\s+/g, '+');
      setSubmitFontImportUrl(`@import url('https://fonts.googleapis.com/css2?family=${formattedFontLabel}:wght@100;200;300;400;500;600;700;800;900&display=swap');`);
    }
  }, [state.submitFontFamily]);

  const [fieldLabelFontImportUrl, setFieldLabelFontImportUrl] = useState('');
  useEffect(() => {
    if (state.fieldLabelFontFamily) {
      const fontFamilyLabel = fontList.find(font => font.value === state.fieldLabelFontFamily)?.label || 'sans-serif';
      const formattedFontLabel = fontFamilyLabel.replace(/\s+/g, '+');
      setFieldLabelFontImportUrl(`@import url('https://fonts.googleapis.com/css2?family=${formattedFontLabel}:wght@100;200;300;400;500;600;700;800;900&display=swap');`);
    }
  }, [state.fieldLabelFontFamily]);

  const [descriptionFontImportUrl, setDescriptionFontImportUrl] = useState('');
  useEffect(() => {
    if (state.descriptionFontFamily) {
      const fontFamilyLabel = fontList.find(font => font.value === state.descriptionFontFamily)?.label || 'sans-serif';
      const formattedFontLabel = fontFamilyLabel.replace(/\s+/g, '+');
      setDescriptionFontImportUrl(`@import url('https://fonts.googleapis.com/css2?family=${formattedFontLabel}:wght@100;200;300;400;500;600;700;800;900&display=swap');`);
    }
  }, [state.descriptionFontFamily]);

  const [checkboxLabelFontImportUrl, setCheckboxLabelFontImportUrl] = useState('');
  useEffect(() => {
    if (state.checkboxLabelFontFamily) {
      const fontFamilyLabel = fontList.find(font => font.value === state.checkboxLabelFontFamily)?.label || 'sans-serif';
      const formattedFontLabel = fontFamilyLabel.replace(/\s+/g, '+');
      setCheckboxLabelFontImportUrl(`@import url('https://fonts.googleapis.com/css2?family=${formattedFontLabel}:wght@100;200;300;400;500;600;700;800;900&display=swap');`);
    }
  }, [state.checkboxLabelFontFamily]);

  const [radioLabelFontImportUrl, setRadioLabelFontImportUrl] = useState('');
  useEffect(() => {
    if (state.radioLabelFontFamily) {
      const fontFamilyLabel = fontList.find(font => font.value === state.radioLabelFontFamily)?.label || 'sans-serif';
      const formattedFontLabel = fontFamilyLabel.replace(/\s+/g, '+');
      setRadioLabelFontImportUrl(`@import url('https://fonts.googleapis.com/css2?family=${formattedFontLabel}:wght@100;200;300;400;500;600;700;800;900&display=swap');`);
    }
  }, [state.radioLabelFontFamily]);

  

  

  const javascriptCode = generateScript(state);

  const getFormattedCSS = () => {
    const dynamicStyles = `
    ${inputFontImportUrl} 

    ${selectFontImportUrl}

    ${textAreaFontImportUrl}

    ${fieldLabelFontImportUrl}

    ${descriptionFontImportUrl}

    ${placeholderFontImportUrl}

    ${checkboxLabelFontImportUrl}

    ${radioLabelFontImportUrl}

    ${submitFontImportUrl}

    

    

   

    :root {
        --input-height: ${state.inputHeight}px;
        --input-width: ${state.inputWidth}${state.inputWidthUnits};
        --input-border-color: ${state.inputBorderColor};
        --input-border-width: ${state.inputBorderWidth}px;
        --input-border-style: ${state.inputBorderStyleCheckbox};
        --input-padding: ${state.inputPaddingInline}px;
        --input-border-radius: ${state.inputBorderRadius}px;
        --input-color: ${state.inputColor};
        --input-font-size: ${state.inputFontSize}px;
        --input-font-weight: ${state.inputFontWeight};
        --field-input-width: ${inputFlexBasis};
        --input-font-family: ${state.inputFontFamily};
        --input-hover-color: ${state.inputHoverColor};
        --input-hover-border-color: ${state.inputHoverBorderColor};
        --input-hover-background-color: ${state.inputHoverBackgroundColor};
        --field-top-spacing: ${state.fieldTopSpacing}px;
        --input-background-color: ${state.inputBackgroundColor};

        --select-height: ${state.selectHeight}px;
        --select-width: ${state.selectWidth}${state.selectWidthUnits};
        --select-border-color: ${state.selectBorderColor};
        --select-border-width: ${state.selectBorderWidth}px;
        --select-border-style: ${state.selectBorderStyleCheckbox};
        --select-padding: ${state.selectPaddingInline}px;
        --select-border-radius: ${state.selectBorderRadius}px;
        --select-color: ${state.selectColor};
        --select-font-size: ${state.selectFontSize}px;
        --select-font-weight: ${state.selectFontWeight};
        --select-font-family: ${state.selectFontFamily};
        --select-hover-color: ${state.selectHoverColor};
        --select-hover-border-color: ${state.selectHoverBorderColor};
        --select-hover-background-color: ${state.selectHoverBackgroundColor};
        --select-top-spacing: ${state.selectTopSpacing}px;
        --select-background-color: ${state.selectBackgroundColor};
        --select-background-image: url('${state.selectBackgroundImage}');
        --select-background-image-position-right: ${state.selectBackgroundImagePositionRight}px;
        --select-background-image-size: ${state.selectBackgroundImageSize}px;
        --select-appearance: ${state.addSelectBackgroundImage ? "none;" : "auto"};

        --textarea-height: ${state.textAreaHeight}px;
        --textarea-width: ${state.textAreaWidth}${state.textAreaWidthUnits};
        --textarea-border-color: ${state.textAreaBorderColor};
        --textarea-border-width: ${state.textAreaBorderWidth}px;
        --textarea-border-style: ${state.textareaBorderStyleCheckbox};
        --textarea-padding: ${state.textAreaPaddingInline}px;
        --textarea-border-radius: ${state.textAreaBorderRadius}px;
        --textarea-color: ${state.textAreaColor};
        --textarea-font-size: ${state.textAreaFontSize}px;
        --textarea-font-weight: ${state.textAreaFontWeight};
        --textarea-font-family: ${state.textAreaFontFamily};
        --textarea-hover-color: ${state.textAreaHoverColor};
        --textarea-hover-border-color: ${state.textAreaHoverBorderColor};
        --textarea-hover-background-color: ${state.textAreaHoverBackgroundColor};
        --textarea-top-spacing: ${state.textAreaTopSpacing}px;
        --textarea-background-color: ${state.textAreaBackgroundColor};

        --field-label-font-family: ${state.fieldLabelFontFamily};
        --field-label-font-size: ${state.fieldLabelFontSize}px;
        --field-label-font-weight: ${state.fieldLabelFontWeight};
        --field-label-bottom-spacing: ${state.fieldLabelBottomSpacing}px;
        --field-label-padding: ${state.fieldLabelPadding}px;
        --field-label-color: ${state.fieldLabelColor};
        --field-label-color-asterisk: ${state.fieldLabelColorAsterisk};
        --field-label-alignment: ${state.fieldLabelAlignment};
        --field-label-width: ${labelFlexBasis};

        --description-font-family: ${state.descriptionFontFamily};
        --description-font-size: ${state.descriptionFontSize}px;
        --description-font-weight: ${state.descriptionFontWeight};
        --description-color: ${state.descriptionColor};
        --description-top-spacing: ${state.descriptionTopSpacing}px;
        --description-padding: ${state.descriptionPaddingInline}px;

        --placeholder-color: ${state.placeholderColor};
        --placeholder-hover-color: ${state.placeholderHoverColor};
        --placeholder-font-family: ${state.placeholderFontFamily};
        --placeholder-font-size: ${state.placeholderFontSize}px;
        --placeholder-font-weight: ${state.placeholderFontWeight};

        --checkbox-input-size: ${state.checkboxInputSize}px;
        --checkbox-border-width: ${state.checkboxBorderWidth}px;
        --checkbox-border-radius: ${state.checkboxBorderRadius}px;
        --checkbox-unchecked-border-color: ${state.checkboxUncheckedBorderColor};
        --checkbox-unchecked-background: ${state.checkboxUncheckedBackgroundColor};
        --checkbox-checked-background: ${state.checkboxCheckedBackgroundColor};
        --checkbox-checked-border-color: ${state.checkboxCheckedBorderColor};
        --checkbox-checked-color: ${state.checkboxCheckedColor};
        --checkbox-checked-content: "${state.checkedCheckboxContent}";
        --checkbox-alignment: ${state.checkboxAlignment};
        --checkbox-label-font-family: ${state.checkboxLabelFontFamily};
        --checkbox-label-font-size: ${state.checkboxLabelFontSize}px;
        --checkbox-label-font-weight: ${state.checkboxLabelFontWeight};
        --checkbox-label-color: ${state.checkboxLabelColor};

        --radio-input-size: ${state.radioInputSize}px;
        --radio-border-width: ${state.radioBorderWidth}px;
        --radio-border-radius: ${state.radioBorderRadius}px;
        --radio-unchecked-border-color: ${state.radioUncheckedBorderColor};
        --radio-unchecked-background: ${state.radioUncheckedBackgroundColor};
        --radio-checked-background: ${state.radioCheckedBackgroundColor};
        --radio-checked-border-color: ${state.radioCheckedBorderColor};
        --radio-alignment: ${state.radioAlignment};
        --radio-label-font-family: ${state.radioLabelFontFamily};
        --radio-label-font-size: ${state.radioLabelFontSize}px;
        --radio-label-font-weight: ${state.radioLabelFontWeight};
        --radio-label-color: ${state.radioLabelColor};

        --toggle-margin-bottom: ${state.toggleMarginBottom}px;
        --toggle-margin-right: ${state.toggleMarginRight}px;
        --toggle-position: ${state.togglePosition};
        --toggle-width: ${state.toggleWidth}px;
        --toggle-height: ${state.toggleHeight}px;
        --toggle-background-color: ${state.toggleBackgroundColor};
        --toggle-border-width: ${state.toggleBorderWidth}px;
        --toggle-border-color: ${state.toggleBorderColor};
        --toggle-border-radius: ${state.toggleBorderRadius}px;

        --toggle-checked-background-color: ${state.toggleCheckedBackgroundColor};

        --toggle-knob-width: ${state.toggleKnobWidth}px;
        --toggle-knob-height: ${state.toggleKnobHeight}px;
        --toggle-knob-color: ${state.toggleKnobColor};
        --toggle-knob-alignment: ${state.toggleKnobAlignment}px;

        --toggle-knob-checked-color: ${state.toggleKnobCheckedColor};
        --toggle-knob-checked-alignment-left: ${state.toggleKnobCheckedAlignmentLeft}px;
        --toggle-knob-checked-alignment-top: ${state.toggleKnobCheckedAlignmentTop}%;

        --toggle-label-font-family: ${state.toggleLabelFontFamily};
        --toggle-label-font-size: ${state.toggleLabelFontSize}px;
        --toggle-label-font-weight: ${state.toggleLabelFontWeight};
        --toggle-label-color: ${state.toggleLabelColor};
        

        --submit-margin-top: ${state.submitMarginTop}px;
        --submit-font-size:  ${state.submitFontSize}px;
        --submit-font-family:  ${state.submitFontFamily};
        --submit-width:  ${state.submitWidth}${state.submitWidthUnits};
        --submit-height: ${state.submitHeight}px;
        --submit-border-radius:  ${state.submitBorderRadius}px;
        --submit-border-width:  ${state.submitBorderWidth}px;
        --submit-background:  ${state.submitBackground};
        --submit-color: ${state.submitColor};
        --submit-border-color: ${state.submitBorderColor};
        --submit-background-hover: ${state.submitBackgroundHover};
        --submit-color-hover: ${state.submitColorHover};
        --submit-border-color-hover: ${state.submitBorderColorHover};
        --submit-text-align: ${state.submitTextAlign};
        --submit-font-weight: ${state.submitFontWeight};
        --submit-background-image: url('${state.submitBackgroundImage}');
        --submit-background-image-position-right: ${state.submitBackgroundImagePositionRight}px;
        --submit-background-image-size: ${state.submitBackgroundImageSize}px;
        --submit-alignment: ${state.submitButtonAlign};

        --error-text-font-size:  ${state.errorTextFontSize}px;
        --error-text-font-family:  ${state.errorTextFontFamily};
        --error-text-font-weight: ${state.errorTextFontWeight};
        --error-text-color: ${state.errorTextColor};
        --error-text-top-spacing: ${state.errorTextTopSpacing}px;
        --error-text-padding: ${state.errorTextPaddingInline}px;
        
        --error-heading-font-size:  ${state.errorHeadFontSize}px;
        --error-heading-font-family:  ${state.errorHeadFontFamily};
        --error-heading-font-weight: ${state.errorHeadFontWeight};
        --error-heading-color: ${state.errorHeadColor};
        --error-heading-bottom-spacing: ${state.errorHeadBottomSpacing}px;
        --error-heading-padding: ${state.errorHeadPaddingInline}px;
        --error-heading-bg-color:  ${state.errorHeadBackground};

        --recaptcha-alignment: ${state.recaptchaAlignment};
        
        --transition-main: ${state.transitionMain};
      }
      `;

      

    return `<style>${resetStyles + dynamicStyles + staticStyles}</style>`;
  };

  const stylesString = getFormattedCSS();
  
  const finalCode = stylesString + htmlCode + javascriptCode;

  const handleCopyToClipboard = async () => {
    const formattedCSS = getFormattedCSS();
    try {
      await navigator.clipboard.writeText(finalCode);
      console.log("CSS copied to clipboard");
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (err) {
      console.error("Failed to copy CSS: ", err);
    }
  };



  return (
    <StyledEngineProvider>
      <div className="form-builder-section-wrapper">
      <section className="form-builder-clipboard-section">
        <div className="main-headline">Pardot Form Styling App</div>
        </section>
        <section className="form-builder-form-section">
          <FieldGroupsAccordion />
          <div>
            <FormPreview />
            
            
          </div>
        </section>
        <section className="form-builder-clipboard-section">
        <div class="form-clipboard">
        <button
              onClick={handleCopyToClipboard}
              className={`clipboard-btn ${isCopied ? "copied" : ""}`}
            >
              {isCopied ? "Copied!" : "Copy CSS to Clipboard"}
            </button>
        <textarea
              value={finalCode}
              readOnly
              rows="10"
              cols="50"
              className={`styles-textarea ${isCopied ? "copied" : ""}`}
            />
        </div>
        </section>
      </div>
      <br />
    </StyledEngineProvider>
  );
};

export default function FormBuilder() {
  return (
    <FormBuilderContextProvider>
      <FormBuilderScreen />
    </FormBuilderContextProvider>
  );
}
